import { useCallback, useMemo } from 'react';

import { ContractRole, useAuth } from '../components/auth/AuthContext';
import { useApplicantApplicationAccess } from './useApplicantApplicationAccess';

/**
 * Per-contract role types accepted by useRBAC predicates.
 *
 * Source of truth: `ContractRole['role_type']` in AuthContext, which mirrors
 * `docs/api/auth-api-spec.yaml` (UserContractRole.role_type). The `satisfies`
 * constraint guarantees `CONTRACT_ROLE` cannot drift out of that union — TS
 * fails at build time if a value here is not a valid `role_type`.
 */
export const CONTRACT_ROLE = {
  COR: 'COR',
  FEDERAL_APPROVER: 'FEDERAL_APPROVER',
  FOREIGN_NATIONAL_APPROVER: 'FOREIGN_NATIONAL_APPROVER',
  ROSTER_OWNER: 'ROSTER_OWNER',
} as const satisfies Record<string, ContractRole['role_type']>;

export type ContractRoleType = ContractRole['role_type'];

export const SYSTEM_ROLE = {
  ADMINISTRATOR: 'ADMINISTRATOR',
  COR: 'COR',
  FOREIGN_NATIONAL: 'FOREIGN_NATIONAL',
  SERVICE_DESK: 'SERVICE_DESK',
} as const;

export type SystemRoleType = (typeof SYSTEM_ROLE)[keyof typeof SYSTEM_ROLE];

export interface RBACCapabilities {
  /** Basic contractors and Roster Owners participate in the eNOVA applicant experience. */
  canAccessApplication: boolean;
  canAccessApplicationStatus: boolean;
  /** Global Contracts entry point; per-contract authorization still applies. */
  canAccessContracts: boolean;
  /** Global Approvals entry point; the API remains authoritative for visible work. */
  canAccessApprovals: boolean;
  hasElevatedRole: boolean;
}

export interface UseRBACResult {
  /** Contract-scoped roles for the current user. Always an array, possibly empty. */
  roles: ContractRole[];
  /** System-wide roles for the current user. Always an array, possibly empty. */
  systemRoles: SystemRoleType[];
  /** True if the user has the system-wide Administrator role. */
  isAdmin: boolean;
  /** Global page/navigation capabilities derived from system and contract roles. */
  capabilities: RBACCapabilities;
  /**
   * True while AuthContext or applicant application-state access is loading.
   *
   * Predicates are NOT gated on `isLoading`: during a background re-verify they
   * keep returning results based on the most recent known roles, so consumers
   * do not flicker. Use `isLoading` if you want to render a skeleton instead.
   */
  isLoading: boolean;
  /** True if the user has the given role on the given contract. */
  hasRoleOnContract: (contractId: string, role: ContractRoleType) => boolean;
  /** True if the user has any of the given roles on the given contract. */
  hasAnyRoleOnContract: (contractId: string, roles: ContractRoleType[]) => boolean;
  /** All roles the user has on the given contract. May be empty. */
  getRolesForContract: (contractId: string) => ContractRole[];
  /** True if the user has the given system-wide role. */
  hasSystemRole: (role: SystemRoleType) => boolean;
}

/**
 * Hook for role-based conditional rendering.
 * Authorization is enforced server-side via OPA — this hook is for UX hints only.
 *
 * @example Gate COR-only action in Contract Roster
 * const { hasRoleOnContract } = useRBAC();
 * if (hasRoleOnContract(contractId, CONTRACT_ROLE.COR)) {
 *   return <AssignRosterOwnerButton />;
 * }
 */
export const useRBAC = (): UseRBACResult => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { hasActiveApplication, isLoading: isApplicationStateLoading } = useApplicantApplicationAccess();

  const roles = useMemo<ContractRole[]>(() => user?.roles ?? [], [user?.roles]);
  const effectiveRoles = useMemo(
    () =>
      roles.filter(
        (role) =>
          role.role_type !== CONTRACT_ROLE.COR || role.role_variant !== 'ALTERNATE' || role.is_effective === true,
      ),
    [roles],
  );
  const systemRoles = useMemo<SystemRoleType[]>(
    () =>
      Array.isArray(user?.system_roles)
        ? user.system_roles.filter((role): role is SystemRoleType =>
            Object.values(SYSTEM_ROLE).includes(role as SystemRoleType),
          )
        : [],
    [user?.system_roles],
  );

  const hasRoleOnContract = useCallback(
    (contractId: string, role: ContractRoleType): boolean => {
      if (!contractId) return false;

      return effectiveRoles.some((r) => r.contract_id === contractId && r.role_type === role);
    },
    [effectiveRoles],
  );

  const hasAnyRoleOnContract = useCallback(
    (contractId: string, rolesList: ContractRoleType[]): boolean => {
      if (!contractId) return false;
      if (rolesList.length === 0) return false;

      return effectiveRoles.some((r) => r.contract_id === contractId && rolesList.includes(r.role_type));
    },
    [effectiveRoles],
  );

  const getRolesForContract = useCallback(
    (contractId: string): ContractRole[] => {
      if (!contractId) return [];

      return effectiveRoles.filter((r) => r.contract_id === contractId);
    },
    [effectiveRoles],
  );

  const hasSystemRole = useCallback((role: SystemRoleType): boolean => systemRoles.includes(role), [systemRoles]);
  const isAdmin = hasSystemRole(SYSTEM_ROLE.ADMINISTRATOR);
  const hasContractRole = useCallback(
    (roleTypes: ContractRoleType[]) => roles.some((role) => roleTypes.includes(role.role_type)),
    [roles],
  );
  const hasEffectiveContractRole = useCallback(
    (roleTypes: ContractRoleType[]) => effectiveRoles.some((role) => roleTypes.includes(role.role_type)),
    [effectiveRoles],
  );
  const isFederalApprover =
    hasSystemRole(SYSTEM_ROLE.COR) || hasContractRole([CONTRACT_ROLE.COR, CONTRACT_ROLE.FEDERAL_APPROVER]);
  const isForeignNationalApprover = hasSystemRole(SYSTEM_ROLE.FOREIGN_NATIONAL);
  const isRosterOwner = hasEffectiveContractRole([CONTRACT_ROLE.ROSTER_OWNER]);
  const hasElevatedRole = isAdmin || isFederalApprover || isForeignNationalApprover || isRosterOwner;
  const canAccessApplicantExperience = !hasElevatedRole || isRosterOwner;

  const capabilities = useMemo<RBACCapabilities>(() => {
    return {
      canAccessApplication: canAccessApplicantExperience,
      canAccessApplicationStatus: canAccessApplicantExperience && hasActiveApplication,
      canAccessApprovals: isAdmin || isFederalApprover || isForeignNationalApprover,
      canAccessContracts:
        isAdmin ||
        hasSystemRole(SYSTEM_ROLE.COR) ||
        hasEffectiveContractRole([CONTRACT_ROLE.COR, CONTRACT_ROLE.FEDERAL_APPROVER, CONTRACT_ROLE.ROSTER_OWNER]),
      hasElevatedRole,
    };
  }, [
    canAccessApplicantExperience,
    hasActiveApplication,
    hasElevatedRole,
    isAdmin,
    isFederalApprover,
    isForeignNationalApprover,
    isRosterOwner,
    hasEffectiveContractRole,
    hasSystemRole,
  ]);

  return {
    capabilities,
    getRolesForContract,
    hasAnyRoleOnContract,
    hasRoleOnContract,
    hasSystemRole,
    isAdmin,
    isLoading: isAuthLoading || isApplicationStateLoading,
    roles,
    systemRoles,
  };
};
